/** Typed client for the Velma API. Every call carries a bearer token; nothing uses cookies. */
import { getToken } from "../auth";

const BASE = import.meta.env.VITE_API_BASE ?? "http://localhost:8000";

/** sessionStorage on purpose, even though the sign-in itself now lives in localStorage.
 *  Impersonation should end with the tab, not follow an admin into every tab they open. */
const ACT_AS_KEY = "cortex.actAs";

export function getActAs(): string | null {
  try {
    return sessionStorage.getItem(ACT_AS_KEY);
  } catch {
    return null;
  }
}

export function setActAs(email: string | null): void {
  try {
    if (email) sessionStorage.setItem(ACT_AS_KEY, email);
    else sessionStorage.removeItem(ACT_AS_KEY);
  } catch {
    /* private mode / disabled storage */
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

function authHeaders(extra?: HeadersInit): Record<string, string> {
  const headers: Record<string, string> = { ...(extra as Record<string, string> | undefined) };
  const actAs = getActAs();
  if (actAs) headers["X-Cortex-Act-As"] = actAs;
  return headers;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...authHeaders(init.headers),
    },
  });
  if (!response.ok) {
    // Stale impersonation target (manager demoted/removed): drop it and retry once so the
    // admin is not stuck behind a 400 on every /api/me until they clear storage by hand.
    if (response.status === 400 && path === "/api/me" && getActAs()) {
      setActAs(null);
      return request<T>(path, init);
    }
    // FastAPI puts the reason in `detail`; fall back to the status text if the body isn't JSON.
    let detail = response.statusText;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail);
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

/** Same auth/error handling as request(), but returns the raw Response instead of assuming a
 * JSON body -- for endpoints that hand back a file (e.g. the Audit Log's Excel export). getToken()
 * is a bearer header, not a cookie, so a plain `<a href>` straight at the backend URL wouldn't
 * authenticate; callers fetch through here first, then trigger the save from the resulting Blob. */
async function requestBlob(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await getToken();
  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${token}`, ...authHeaders(init.headers) },
  });
  if (!response.ok) {
    let detail = response.statusText;
    try {
      detail = (await response.json()).detail ?? detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(response.status, detail);
  }
  return response;
}

// ---------------------------------------------------------------- types

export interface Me {
  oid: string | null;
  email: string | null;
  name: string | null;
  is_admin: boolean;
  is_manager: boolean;
  /** Query-topic slugs assigned to this Manager. Empty for Admins (they see every topic). */
  assigned_tags: string[];
  /** Token is an Admin, even while is_admin is false because of View as. */
  real_is_admin: boolean;
  /** Manager email currently being viewed as, or null. */
  acting_as: string | null;
  /** Dev-lockdown gate. Only the deployed dev environment ever returns false here (its
   * CORTEX_ALLOWED_ORIGINS contains a dev SPA domain -- velma.dev or cortex.dev -- which
   * rag/dev_gate.gate_active() uses as the positive "am I the deployed dev environment?"
   * signal). Prod, local, and every other environment always return true here. When
   * false, the caller's email isn't in rag/dev_gate.py's hardcoded _ALLOWLIST and the
   * router guard bounces them to /dev-blocked with a "please use production" panel.
   * Optional so any cached /api/me shape from an older backend build defaults to allowed. */
  dev_allowed?: boolean;
}

export interface ActAsTarget {
  email: string;
  oid: string | null;
  tags: string[];
}

export interface Source {
  label: string;
  url?: string | null;
  source_type?: string | null;
  /** The source content's own last-updated date (ISO 8601), not when Velma indexed it --
   * null when no date is known for this source (e.g. most misc uploads). */
  source_updated_at?: string | null;
  /** Lets the frontend fetch this citation's full document via documentChunks() -- e.g. the
   * Brandland "view raw text" popup. Absent on messages saved before this field existed. */
  doc_id?: string | null;
  /** Brandland-specific folder breadcrumb (e.g. category "departments" + department "Beyond
   * Broadline"); null/absent for every other source type. */
  category?: string | null;
  department?: string | null;
  /** Brandland's own "created" date (from the source PDF), distinct from source_updated_at
   * (which is "last updated"). */
  pdf_creation_date?: string | null;
  /** Provenance the uploader typed into the Add-knowledge form ("Brandland", "brand website",
   * "personal knowledge", ...) -- migration 0033, feature request 2026-08-27. Empty string
   * or absent for chunks whose source type has no human uploader (Brandland/handbook/Jira/
   * Confluence) or that were ingested before the field existed. */
  source_label?: string | null;
  /** Email of the person who submitted this content through the Add-knowledge form,
   * auto-captured from identity.caller.email at request time -- migration 0033, same feature.
   * Same rendering rules as source_label above: absent for non-misc source types, and for
   * pre-feature or script-imported misc rows. */
  uploaded_by?: string | null;
}

export interface ContextChunk extends Source {
  text?: string;
  /** Cosine distance: **lower is more relevant**, the opposite of a score. */
  distance?: number | null;
}

/** Why a question was judged out of scope. `off_topic` = has nothing to do with BEP.
 * `unsupported_action` = IS about BEP, but asks Velma to do something (create a ticket, send
 * an email, act in Salesforce, draft/send a document, ...) rather than answer a question --
 * Velma is read-only and has no side effects. `personal_information` = asks for a personal/
 * biographical attribute (age, health, family, home address, ...) about a named individual,
 * regardless of topic -- distinct from that person's professional details, which stay in scope.
 * Null whenever in_scope is true. */
export type OutOfScopeKind = "off_topic" | "unsupported_action" | "personal_information";

export interface Answer {
  answer: string;
  sources: Source[];
  confident: boolean | null;
  /** `hard` = Velma said it wasn't sure or found nothing; `soft` = confident but weakly grounded. */
  severity: "hard" | "soft" | null;
  /** false = Velma judged the question itself out of scope (see out_of_scope_kind for why) --
   * never a knowledge gap, tracked separately (see OutOfScopeCluster). Null only for responses
   * predating this. */
  in_scope: boolean | null;
  out_of_scope_kind: OutOfScopeKind | null;
  context_chunks: ContextChunk[];
  message_id: string | null;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  message_count: number;
}

export interface MessageFeedback {
  rating: "up" | "down";
  comment: string | null;
}

export interface Message {
  id: string | null;
  role: "user" | "assistant";
  content: string;
  sources: Source[] | null;
  confident: boolean | null;
  severity: "hard" | "soft" | null;
  gap_key?: string | null;
  in_scope: boolean | null;
  out_of_scope_kind: OutOfScopeKind | null;
  created_at: string;
  feedback: MessageFeedback | null;
  tags?: MessageTag[];
}

export interface Conversation extends ConversationSummary {
  messages: Message[];
}

export interface MessageTag {
  tag: string;
  source: "auto" | "manual";
}

export interface Exchange {
  id: string;
  created_at: string;
  question: string;
  answer: string;
  sources: Source[] | null;
  confident: boolean | null;
  min_distance: number | null;
  severity: "hard" | "soft" | null;
  gap_key: string | null;
  in_scope: boolean | null;
  out_of_scope_kind: OutOfScopeKind | null;
  rewritten_query: string | null;
  channel: string;
  user_oid: string;
  user_email: string | null;
  cortex_version: string | null;
  /** The parent conversation, or null for single-turn surfaces (MCP). Both the Question Feed
   * and the Query log load the surrounding thread from this for "View chat history"; a null
   * falls back to fetching by this row's own message id, which yields just this exchange. */
  conversation_id: string | null;
  /** Department/topic tags -- auto-assigned live, correctable by an Admin or Manager. */
  tags: MessageTag[];
  /** The live "answer was already fine" verdict covering this row's gap_key, or null.
   *
   * Needed because `severity` above is written once, at ask time, and never revisited -- so a
   * row keeps saying "Soft gap" long after someone approved the answer, which reads as open
   * work that has mysteriously vanished from the Manager Inbox. Only this verdict is carried,
   * not resolutions in general: it is the one that also stops future similar questions being
   * flagged, so it changes what the row means rather than merely recording a decision. */
  sufficient: { marked_by: string; marked_at: string } | null;
}

/** One entry in the query-topic tag taxonomy (rag/taxonomy.py's query_topic category) -- what
 * the auto-tagger's LLM call classifies against, and what the Log view's tag picker offers.
 * DB-backed and editable by an Admin or Manager, not a hardcoded list. */
export interface QueryTagDefinition {
  slug: string;
  label: string;
  description: string;
  active: boolean;
}

/** How many distinct questions carry a tag, in a date range -- the Admin Dashboard's "what are
 * people asking about" breakdown. */
export interface TagStat {
  tag: string;
  question_count: number;
}

/** One row on the Manager Topic Insights page (/admin/insights). Volume + flagged are cross-
 * tag; open_gaps + thumbs_down are assigned-tag-only and come back as `null` on rows the
 * viewer isn't assigned to -- the frontend renders those as an em-dash so "no signal for me
 * to act on" reads distinctly from "0". Admins are treated as assigned to every tag (see
 * rag/topic_insights.py -- unrestricted scope == you own everything). */
export interface TopicInsightRow {
  tag: string;
  label: string;
  assigned_to_viewer: boolean;
  total_questions: number;
  share: number;
  open_gaps: number | null;
  thumbs_down: number | null;
  flagged: number;
}

export interface TopicInsightsResponse {
  topics: TopicInsightRow[];
}

export interface ChannelCount {
  channel: string;
  exchanges: number;
  distinct_users: number;
}

export interface ExchangePage {
  total: number;
  exchanges: Exchange[];
}

export interface MessageUser {
  user_oid: string;
  user_email: string | null;
  channels: string[];
  exchanges: number;
  first_seen: string;
  last_seen: string;
}

/** One person on the Users page: their role and their question activity in a single row.
 *
 * Distinct from MessageUser above, which is activity only and so can't see an Admin who has
 * never asked anything. Built by rag/user_directory.py, which merges the two sources the
 * backend keeps separate (the role table, keyed by email, and the message log, grouped by oid). */
export interface UserDirectoryRow {
  email: string | null;
  oid: string | null;
  role: Role | "user";
  /** Domain after the "@", which is the closest thing to a brand this data has. The literal
   * "(no email)" for MCP/eval callers, so it stays a usable filter value rather than a blank. */
  brand: string;
  channels: string[];
  questions: number;
  first_seen: string | null;
  /** Null for someone who holds a role but has never asked anything. */
  last_seen: string | null;
  added_by: string | null;
  added_at: string | null;
  tags: string[];
}

export interface UserDirectory {
  users: UserDirectoryRow[];
  /** Every brand in the directory, not just the ones surviving the current filter -- otherwise
   * picking one would leave no way back to the others. */
  brands: string[];
  /** Unfiltered headcount, for an "N of M" readout. */
  total: number;
}

export interface UserDirectoryFilters {
  search?: string;
  brand?: string;
  channel?: string;
  role?: string;
  sort?: string;
  direction?: string;
}

/** Someone the Admin Dashboard leaves out of its statistics -- the team's own testers and QA,
 * whose hand-testing is indistinguishable from real use at the row level. They keep counting on
 * the ROI Estimate page and in LLM spend; see rag/stats_exclusions.py for the full split. */
export interface StatsExclusion {
  email: string;
  excluded_by: string;
  excluded_at: string;
}

/** Shared by the list and the export so the spreadsheet can't disagree with the screen. */
function userDirectoryParams(f: UserDirectoryFilters): string {
  return new URLSearchParams({
    ...(f.search ? { search: f.search } : {}),
    ...(f.brand ? { brand: f.brand } : {}),
    ...(f.channel ? { channel: f.channel } : {}),
    ...(f.role ? { role: f.role } : {}),
    ...(f.sort ? { sort: f.sort } : {}),
    ...(f.direction ? { direction: f.direction } : {}),
  }).toString();
}

export interface Gap {
  gap_key: string;
  occurrences: number;
  first_seen: string;
  last_seen: string;
  /** The most recent occurrence's message.id -- gap_key alone can't be reveal-keyed
   * because reveal is per-message, not per-cluster. Projected from gap_summary()'s
   * DISTINCT ON so it matches whichever occurrence example_question/answer/user_email
   * came from. Null only in the theoretical case that gap_summary()'s SELECT drops the
   * column (which the query-level test in tests/test_gap_queries.py locks in). */
  message_id: string | null;
  /** The conversation the most recent occurrence was asked in, so the row can open the full
   * chat history the same way the query log does. Null for exchanges with no conversation
   * (MCP), where conversationThread() falls back to synthesizing the single exchange. */
  conversation_id: string | null;
  example_question: string;
  answer: string;
  severity: "hard" | "soft";
  confident: boolean | null;
  min_distance: number | null;
  /** What the latest occurrence's answer cited. The signal for whether there is a real
   * grounded answer here at all -- `severity` can't tell you, since 'hard' covers both
   * "retrieved nothing" and "hedged despite good retrieval". */
  sources: Source[] | null;
  /** The most recent occurrence's asker -- same DISTINCT ON as example_question/answer, not
   * every asker across all occurrences. Nulled by manager_scope.redact_askers() on the
   * server for both admins and managers -- reveal per-row via GET
   * /api/admin/questions/{message_id}/asker. */
  user_email: string | null;
  user_oid: string | null;
  status: "open" | "resolved";
  note: string | null;
  /** Why a resolved gap was closed -- null while open. Required by the API when resolving.
   * `answer_sufficient` is the only one that also suppresses future flagging (see
   * sufficient_marked_by below); the rest are a record of what was changed. */
  reason:
    | "content_added" | "was_out_of_scope" | "duplicate" | "wont_fix" | "code_fix"
    | "answer_sufficient" | null;
  updated_by: string | null;
  /** Who marked this gap's answer sufficient, and when -- null unless a live verdict exists.
   * Distinct from updated_by/updated_at, which move on any status change: these survive a
   * reopen, and going null is exactly how the row knows a verdict was revoked. */
  sufficient_marked_by: string | null;
  sufficient_marked_at: string | null;
  /** The most recent occurrence's tags -- a recurring gap is several distinct messages, each
   * independently auto-tagged, so this is the latest one's, not a merge across all of them. */
  tags: MessageTag[];
}

/** A recurring question Velma judged out of its domain -- tracked for visibility, never counted
 * as a Knowledge Gap. Almost always correctly declined and needing no action, so the only triage
 * is resolveOutOfScope(): "this one was a misclassification and a code change fixed it". */
export interface OutOfScopeCluster {
  gap_key: string;
  occurrences: number;
  first_seen: string;
  last_seen: string;
  /** The most recent occurrence's message.id -- same reveal-key projection as Gap; see
   * that interface for why gap_key can't be the reveal key. */
  message_id: string | null;
  /** Same purpose as Gap.conversation_id -- see there. */
  conversation_id: string | null;
  example_question: string;
  answer: string;
  out_of_scope_kind: OutOfScopeKind;
  /** The most recent occurrence's asker -- same DISTINCT ON as example_question/answer, not
   * every asker across all occurrences. Nulled server-side for both admins and managers;
   * reveal per-row via GET /api/admin/questions/{message_id}/asker. */
  user_email: string | null;
  user_oid: string | null;
}

/** One document (a Confluence page, or a Jira epic/project rollup) that a scan run added or
 * removed from the index -- see ScanRun.counts. */
export interface ScanPageDetail {
  id: string;
  title: string;
  url: string;
  chunks: number;
}

/** rag.build_index's per-source-type result for one scan run: five per-document counts (kept
 * from before this feature), plus chunk-level totals and added/removed page detail added for
 * the Atlassian Data page. No "updated_pages" list -- content changes on nearly every routine
 * run, so a full list there would be noise, not signal. */
export interface ScanIndexCounts {
  new: number;
  updated: number;
  skipped: number;
  excluded: number;
  deleted: number;
  chunks: { added: number; updated: number; removed: number };
  added_pages: ScanPageDetail[];
  removed_pages: ScanPageDetail[];
}

export interface ConfluenceScanCounts {
  crawl: { spaces: number; pages: number; new_space_keys: string[] };
  index: ScanIndexCounts;
}

interface JiraSummarizeCounts {
  total: number;
  skipped_unchanged: number;
  new: number;
  incremental: number;
  full: number;
}

export interface JiraScanCounts {
  crawl: { projects: number; issues: number };
  epics: JiraSummarizeCounts;
  projects: JiraSummarizeCounts;
  index: { jira_epic: ScanIndexCounts; jira_project: ScanIndexCounts };
}

/** Whether this environment scans on a schedule (rag/nightly_scan.py). Only prod does
 * (docs/adr/0013); dev keeps its data until prod's is copied in. */
export interface ScanSchedule {
  nightly_scan_on: boolean;
  /** When the switch last changed; null if it never has, which means on. */
  changed_at: string | null;
  /** Written by scripts/pull_from_prod.py; null if prod's data was never copied in. */
  copied_from_prod: {
    at: string;
    sources: string[];
    /** Prod's last successful scan of each source when the copy was made. */
    prod_scans: Record<string, string>;
  } | null;
}

/** One row of scripts.run_scan history, for the Atlassian Data admin page. */
export interface ScanRun {
  id: number;
  /** Free text on the backend (scan_run.source has no CHECK) -- "confluence"/"jira" today, but
   * not typed as a literal union so a future source doesn't need an API-client change. */
  source: string;
  status: "running" | "success" | "failed";
  started_at: string;
  finished_at: string | null;
  /** {} while running, or on failure -- shape otherwise depends on `source`. */
  counts: ConfluenceScanCounts | JiraScanCounts | EgnyteScanCounts | OrgChartScanCounts | Record<string, never>;
  /** Full traceback string on failure (multi-line), null otherwise. */
  error: string | null;
}

/** One file an Egnyte scan touched, in a run's counts (rag/egnyte_sync.py _detail()). */
export interface EgnyteFileDetail {
  group_id: string | null;
  name: string;
  /** Relative to the Velma folder, e.g. "Sales_Velma/Deck.pptx". */
  path: string;
  department: string;
  method: string | null;
  pages: number | null;
  images: number | null;
  uploaded_by: string | null;
  /** The original file in Egnyte's web UI. */
  url: string | null;
  /** Its .txt in LLM_Converted_Files. */
  txt_url: string | null;
  reason?: string;
  error?: string;
  note?: string;
  /** A new version overwrote a .txt that held SME edits. */
  sme_edits_replaced?: boolean;
}

/** counts for a source = "egnyte" scan run. Every list is capped at 200 entries. */
export interface EgnyteScanCounts {
  trigger: "nightly" | "manual";
  requested_by: string | null;
  root: string;
  departments: string[];
  files: number;
  unchanged: number;
  new: EgnyteFileDetail[];
  updated: EgnyteFileDetail[];
  /** Unchanged files converted again because the converter improved (or another environment
   * sharing the folder did so). Absent from runs before converter versions existed. */
  reconverted?: EgnyteFileDetail[];
  /** Files whose .txt an SME edited, now indexed from the edit. */
  sme_edits: EgnyteFileDetail[];
  /** .txt files that were missing and were rewritten from the indexed text (no model cost). */
  restored: EgnyteFileDetail[];
  moved: EgnyteFileDetail[];
  /** Flagged missing earlier, back in the folder now. */
  returned: EgnyteFileDetail[];
  /** Newly gone from the folder: still answered from until someone confirms removal. */
  missing: EgnyteFileDetail[];
  failed: EgnyteFileDetail[];
  /** Newly skipped by rule (unsupported type, or a same-named PDF beside it). */
  skipped: EgnyteFileDetail[];
  /** Indexed files replaced by a different file uploaded under the same name. */
  replaced: { name: string; path: string }[];
  pages: number;
  images: number;
  cost_usd: number;
  egnyte_api_calls: number;
  index: Partial<ScanIndexCounts>;
  /** Set instead of everything else when the nightly scan found a Scan now already running. */
  skipped_reason?: string;
}

/** One row of /api/admin/egnyte/files: every file the Egnyte scan knows about. */
export interface EgnyteFile {
  group_id: string;
  path: string;
  relative_path: string;
  name: string;
  department: string;
  status: "indexed" | "failed" | "skipped";
  /** Why it was skipped, the failure summary, or conversion notes. */
  status_detail: string | null;
  /** Full traceback of the latest failure. */
  error: string | null;
  method: string | null;
  pages: number | null;
  images: number | null;
  uploaded_by: string | null;
  source_modified: string | null;
  source_size: number | null;
  txt_path: string | null;
  sme_edited: boolean;
  sme_edits_replaced_at: string | null;
  raw_document_id: string | null;
  converted_at: string | null;
  indexed_at: string | null;
  /** Gone from the folder since then; still indexed until someone confirms removal. */
  missing_since: string | null;
  first_seen_at: string;
  chunks: number | null;
  url: string | null;
  txt_url: string | null;
}

/** The org chart export in Egnyte's ADPOrgChart folder, as rag/org_chart_sync.py describes it. */
export interface OrgChartFile {
  path: string;
  name: string;
  group_id: string;
  checksum: string;
  size: number;
  last_modified: string | null;
  uploaded_by: string;
  /** The file in Egnyte's web UI. */
  url: string;
}

/** counts for a source = "org_chart" scan run: one check of the ADPOrgChart folder, nightly or
 * as the first step of Scan now. */
export interface OrgChartScanCounts {
  /** skipped: another import held the lock. */
  outcome: "imported" | "unchanged" | "held" | "no_file" | "skipped";
  /** Set on Scan now's checks only; the nightly run has neither. */
  trigger?: "manual";
  requested_by?: string;
  skipped_reason?: string;
  folder: string;
  file_name: string;
  /** Files beside diningalli.xlsx (other than the README). Never read. */
  other_files: string[];
  /** Absent when outcome is "no_file". */
  file?: OrgChartFile;
  /** Why a held file wasn't imported. */
  reasons?: string[];
  /** People in the file read; null when it couldn't be read at all. */
  people?: number | null;
  /** People in the chart before this run (held, imported). */
  current_people?: number;
  previous_people?: number;
  unresolved_managers?: number;
  egnyte_api_calls: number;
}

/** /api/admin/egnyte/org-chart */
export interface OrgChartImportStatus {
  folder: string;
  file_name: string;
  /** The file the chart was last imported from; null if it never has been, here or on prod. */
  imported: (OrgChartFile & {
    people: number;
    duplicate_names: number;
    unresolved_managers: number;
    imported_at: string;
    /** org_chart_sync_at as that import left it. */
    synced_at: string | null;
  }) | null;
  /** People in the chart now. */
  people: number;
  /** When the chart was last replaced by any route: this import, a manual one, a copy from prod. */
  synced_at: string | null;
  /** Newest first. */
  runs: ScanRun[];
}

export interface DashboardOutOfScopeStats {
  cluster_count: number;
  off_topic_count: number;
  unsupported_action_count: number;
  personal_information_count: number;
}

export interface MessageFeedbackEntry {
  message_id: string;
  /** Same purpose as Gap.conversation_id -- see there. */
  conversation_id: string | null;
  rating: "up" | "down";
  comment: string | null;
  user_oid: string;
  user_email: string | null;
  created_at: string;
  updated_at: string;
  question: string;
  answer: string;
  sources: Source[] | null;
  severity: "hard" | "soft" | null;
  status: "waiting" | "addressed" | "ignored";
  status_note: string | null;
  status_updated_by: string | null;
  status_updated_at: string | null;
}

export interface FeedbackCount {
  rating: "up" | "down";
  count: number;
}

export interface FeedbackStatusCount {
  status: "waiting" | "addressed" | "ignored";
  count: number;
}

export interface DashboardOverview {
  total_questions: number;
  total_users: number;
  /** Sum, across each calendar month touched by the range, of that month's distinct askers --
   * NOT the same as total_users: someone asking in both July and August counts once in
   * total_users but twice here, one per month they'd have otherwise needed a paid seat. Used by
   * the ROI page's license-fee-saved figure. */
  billable_user_months: number;
  /** Share of answers with confident=true, 0..1. Null when no answer in range carries a signal. */
  confident_rate: number | null;
  channels: ChannelCount[];
  /** The whole accountable OpenAI bill for this range: answer_cost + ingest_cost. Summed from
   * actual token counts per call, so it's a measurement rather than an estimate -- except for
   * whatever share estimated_cost reports. There's no backfill, so a range that predates real
   * tracking (or mostly predates it) is correctly low or $0 -- not because nothing was spent,
   * but because nothing from before tracking started was ever recorded. See rag/llm_usage.py. */
  llm_cost: number;
  /** The half of llm_cost spent answering questions -- scales with how much Velma is used. */
  answer_cost: number;
  /** The half of llm_cost spent building and maintaining the corpus: crawling, embedding,
   * tagging, Jira summarization. Scales with corpus size and rebuild frequency, not usage, and
   * was structurally unrecordable before migration 0041. */
  ingest_cost: number;
  /** How much of llm_cost was priced with the conservative fallback rate because the model
   * wasn't in rag/llm_usage.py's PRICING table. Non-zero means that table needs a new entry;
   * surfaced so a guessed figure is never shown as a measured one. */
  estimated_cost: number;
  /** Share of input tokens served from OpenAI's prompt cache at half rate, 0..1. Null when no
   * input tokens were tracked in range. Velma's answer prompt is mostly a large fixed system
   * prompt, so this runs high; a sharp drop means something now varies in the prompt prefix. */
  cached_token_rate: number | null;
  /** answer_cost / priced_questions -- deliberately NOT llm_cost, since averaging a one-off
   * reindex across however many questions happened that month would read as a permanent rise in
   * the cost of asking one. Null when priced_questions is 0 -- nothing to average yet. */
  llm_cost_per_question: number | null;
  /** Real average tokens per tracked query, from the same rows as answer_cost -- split the way
   * OpenAI itself meters and prices them: input (prompt) tokens are the question plus whatever
   * context was retrieved for it; output (completion) tokens are what the model wrote back. Both
   * null when priced_questions is 0. */
  avg_input_tokens_per_query: number | null;
  avg_output_tokens_per_query: number | null;
  /** How many of total_questions actually have tracked usage rows -- almost always fewer until
   * enough time has passed since real tracking began. */
  priced_questions: number;
}

export interface DashboardKnowledgeStats {
  chunk_count: number;
  document_count: number;
  /** On-disk size of the vector table including indexes, in bytes. */
  size_bytes: number;
}

export interface DashboardGapStats {
  open_count: number;
  hard_count: number;
  soft_count: number;
}

export interface DashboardKnowledgeGapRate {
  open_gaps: number;
  total_questions: number;
  // Null on an empty install (or an environment with zero real questions ever recorded) --
  // there's no meaningful ratio to report, and the UI renders that as "—" rather than a
  // misleading "0%" that would read as "no gaps." See rag.messages.dashboard_knowledge_gap_
  // rate()'s docstring.
  rate: number | null;
}

export interface DashboardUiKnowledgeActivity {
  // Approved-only, date-scoped on decided_at, deliberately excluding the admin-only hard-
  // delete path -- see rag.messages.dashboard_ui_knowledge_activity()'s docstring.
  people_added: number;
  people_removed: number;
  chunks_added: number;
  chunks_removed: number;
}

export interface ConversationFeedback {
  conversation_id: string;
  title: string | null;
  up_count: number;
  down_count: number;
}

export interface TimeseriesPoint {
  /** ISO datetime, start of a UTC day. */
  bucket: string;
  channel: string;
  exchanges: number;
  distinct_users: number;
}

export interface LatencyBucket {
  lower_ms: number;
  /** null only on the open-ended top bucket. No threshold named here on purpose: the edges are a
   * backend constant (_LATENCY_BUCKET_EDGES_MS) meant to be retuned once there's real data. */
  upper_ms: number | null;
  count: number;
}

/** One question behind a bar of the Response Speed histogram.
 *
 * `prompt_tokens` and `llm_calls` are here because they are what actually predicts latency:
 * measured over the timed corpus, llm_calls correlates at r=+0.61 and prompt_tokens at r=+0.58,
 * while the length of the question itself is r=+0.06 -- i.e. a long question is not a slow one,
 * but one that makes the pipeline take many sequential LLM round trips is. */
export interface LatencyQuestion {
  id: string;
  conversation_id: string | null;
  created_at: string;
  latency_ms: number;
  channel: string;
  confident: boolean | null;
  severity: string | null;
  /** null only if the question row was lost; the answer still counts toward the bar. */
  question: string | null;
  n_sources: number;
  prompt_tokens: number;
  llm_calls: number;
}

export interface DashboardLatency {
  /** Questions in range with a recorded response time. No backfill (migrations/0019), so this
   * is below total_questions until enough time has passed since instrumentation began. */
  sampled: number;
  /** Shown only in the p50 tile's tooltip, never as its own headline number -- response time is
   * right-skewed, so a mean sits above the typical experience and below the bad one and
   * describes neither. */
  avg_ms: number | null;
  p50_ms: number | null;
  buckets: LatencyBucket[];
}

/** Populated only on a `question` AuditLogEvent -- the full picture of that exchange (answer,
 * gap/scope classification, feedback) that the flat `detail` string alone can't carry. Every
 * field mirrors its Exchange/MessageFeedbackEntry counterpart; null/absent means "not
 * applicable" (e.g. severity is null for a non-gap answer), not "unknown". */
export interface QuestionEventExtra {
  answer: string;
  sources: Source[] | null;
  severity: "hard" | "soft" | null;
  gap_key: string | null;
  /** Only set when severity is non-null and the gap has been triaged; null while open or when
   * this question was never a gap at all. */
  gap_status: "open" | "resolved" | null;
  in_scope: boolean | null;
  out_of_scope_kind: OutOfScopeKind | null;
  feedback_rating: "up" | "down" | null;
  feedback_comment: string | null;
  feedback_status: "waiting" | "addressed" | "ignored" | null;
  /** Which surface the question arrived on. 'eval' never appears -- the audit log filters the
   * eval harness out at the query. Populated on `question` rows only; absent on the
   * `feedback_up`/`feedback_down` rows, whose `extra` carries nothing but `message_id`. */
  channel?: "web" | "mcp" | "api" | null;
  /** The assistant message id backing this row -- passed to /api/admin/questions/{id}/asker
   * so the Audit Log's per-row Reveal button can look up the asker on demand (same audit-
   * logged, one-click-per-audit-row treatment LogView/QuestionFeed/GapsView use). Populated
   * on `question` rows and (as the only key in `extra`) on `feedback_up`/`feedback_down`
   * rows, since the feedback-leaver is almost always the asker and gets redacted alongside. */
  message_id?: string | null;
}

export interface AuditLogEvent {
  created_at: string;
  /** "question" | "feedback_up" | "feedback_down" | "tag_added" | "tag_removed" |
   * "gap_resolved" | "gap_reopened" | "deletion_requested" | "deletion_approved" |
   * "deletion_denied" | "deletion_undone" | "addition_requested" | "addition_approved" |
   * "addition_denied" | "addition_undone" | "document_deleted" | "query_tag_created" |
   * "query_tag_updated" | "feedback_status_changed" | "admin_granted" | "admin_revoked" --
   * left as a plain string rather than a union so a future event source (e.g. a real login
   * signal) doesn't need a type change here. */
  event_type: string;
  /** Null on `question`/`feedback_up`/`feedback_down` rows: those carry an END-USER email,
   * so they're redacted here on the way out and re-revealed on demand via the audit-logged
   * per-row Reveal button (see `extra.message_id`). Non-null on every other row, which
   * carries the ADMIN/MANAGER actor who took the action -- redacting those would defeat
   * the audit trail's whole point. See rag/manager_scope.redact_audit_askers. */
  user_email: string | null;
  detail: string | null;
  /** The question's current tags, or the single tag a tag_added/tag_removed event names --
   * empty for every other event type. */
  tags: string[];
  /** Populated on `question` (full QuestionEventExtra) and `feedback_up`/`feedback_down`
   * (only `message_id`) events -- see QuestionEventExtra. Absent/null on every other event
   * type. */
  extra?: QuestionEventExtra | null;
}

export interface AuditLog {
  total: number;
  events: AuditLogEvent[];
}

export interface AuditLogExportResult {
  blob: Blob;
  /** True if the export hit its row cap and was cut off -- narrowing the date range or filters
   * is the only way to get everything in one file. */
  truncated: boolean;
}

export type Role = "admin" | "manager";

export interface Admin {
  email: string;
  oid: string | null;
  role: Role;
  added_by: string | null;
  added_at: string;
  /** Query-topic slugs this Manager is assigned. Empty for Admins. */
  tags: string[];
}

export interface AssignedWorkItem {
  item_id: string;
  conversation_id: string | null;
  message_id: string | null;
  last_seen: string;
  question: string;
  has_gap: boolean;
  has_thumbs_down: boolean;
  /** True if any assistant message on this thread has an open Manager Question Feed flag
   * (migration 0034). Set alongside has_gap/has_thumbs_down -- a single item can be any
   * combination; the row still dedups on conversation. */
  is_flagged_for_review: boolean;
  /** True if this gap was already declined (resolved + wont_fix). Only ever set when the
   * caller passed include_cant_fix, which the inbox does on its "All" tab so archived rows
   * sit in date order with the open ones instead of trailing the whole list. Mutually
   * exclusive with has_gap: a gap_key is either open or decided, never both. */
  is_cant_fix: boolean;
  /** Who declined it, when, and why. Populated only on is_cant_fix rows. */
  cant_fix_at: string | null;
  cant_fix_by: string | null;
  cant_fix_note: string | null;
  gap_key: string | null;
  severity: "hard" | "soft" | null;
  /** How many times this gap has been asked, across every conversation it appears in. Only
   * meaningful on gap and can't-fix rows (feedback/review rows report 0). */
  occurrences: number;
  user_email: string | null;
  user_oid: string | null;
  feedback_message_ids: string[];
  /** Assistant-message ids on this thread with an open flag -- passed back to
   * skip/undo/mark-reviewed for review-kind actions. */
  review_message_ids: string[];
  tags: MessageTag[];
}

export interface AssignedThread {
  id: string | null;
  user_oid: string | null;
  title: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  messages: Message[];
}

export interface AssignedWorkFillResult {
  fill_request_id: string;
  gap_key: string | null;
  feedback_message_ids: string[];
  review_message_ids: string[];
}

export interface AssignedWorkCantFixResult {
  gap_key: string;
  feedback_message_ids: string[];
  review_message_ids: string[];
}

/** A row on the Manager Inbox's "Can't fix" filter tab -- a gap that was
 * previously marked can't-fix and is now sitting in the resolved/wont_fix state,
 * eligible to be reopened by any manager with visibility on it. Row identity is gap_key
 * (not conversation_id like AssignedWorkItem) because two different gaps on the same
 * thread can be independently reopened. See assigned_work.list_cant_fix() docstring. */
export interface AssignedWorkCantFixItem {
  item_id: string;
  gap_key: string;
  conversation_id: string | null;
  message_id: string | null;
  question: string;
  /** q.created_at of the most-recently-asked instance of this gap, matches the
   * `last_seen` semantics on AssignedWorkItem so both surfaces can share the day-
   * grouping helper in AssignedWorkView. */
  last_seen: string;
  /** knowledge_gap_status.updated_at of the wont_fix marker -- when someone decided
   * this gap wasn't answerable. */
  resolved_at: string;
  /** The email/oid of whoever marked it can't-fix. Rendered under the row as "Marked
   * by X" so the manager reviewing the list can see attribution. */
  resolved_by: string | null;
  /** Optional free-text note the marker attached. Rendered as a quoted block in the
   * row so the current manager can see the prior triage rationale before deciding
   * whether to reopen. */
  note: string | null;
  tags: MessageTag[];
}

/** One gap that was closed by adding knowledge, paired with the knowledge that closed it.
 * Powers the Filled gaps page -- the receipt for the Manager Inbox's "add knowledge"
 * action, which otherwise makes the row vanish with nothing to show for it. Same gap_key
 * row identity and tag scope as AssignedWorkCantFixItem. See assigned_work.list_filled(). */
export interface AssignedWorkFilledItem {
  item_id: string;
  gap_key: string;
  conversation_id: string | null;
  message_id: string | null;
  question: string;
  /** Same `last_seen` semantics as AssignedWorkItem/AssignedWorkCantFixItem. */
  last_seen: string;
  /** knowledge_gap_status.updated_at -- when the gap was marked filled. */
  filled_at: string;
  filled_by: string | null;
  note: string | null;
  /** The addition_request fill() created, or null for a gap someone marked
   * 'content_added' by hand from the admin Gaps page. Every added_* field below is null
   * in that case and the note is the only detail that exists. */
  fill_request_id: string | null;
  added_title: string | null;
  /** The indexed document body. fill() appends "Source(s):"/"Added by:" lines to whatever
   * the manager typed, so this is the text as Velma actually stores it, not the raw input. */
  added_text: string | null;
  added_source: string | null;
  added_document_date: string | null;
  /** Knowledge tags applied to the added document -- distinct from `tags` below, which
   * are the query topics on the question that exposed the gap. */
  added_tags: string[];
  /** addition_request.requested_at. Within a second of filled_at on the fill() path;
   * they diverge only if the addition_request was created separately. */
  added_at: string | null;
  /** 'approved' normally, since fill() auto-approves. 'undone' means the text was later
   * pulled back out of the index even though the gap stayed resolved. */
  addition_status: "pending" | "approved" | "denied" | "undone" | null;
  tags: MessageTag[];
}

/** What POST /api/admin/assigned-work/reopen-cant-fix returns -- the ids that were
 * flipped back so the frontend can offer immediate Undo, and the prior note so the
 * Undo restores the exact original triage state (not a note-less re-cant-fix). */
export interface AssignedWorkReopenResult {
  gap_key: string;
  prior_note: string | null;
  feedback_message_ids: string[];
  review_message_ids: string[];
}

/** A row on the Manager Question Feed (/admin/questions) -- same shape as Exchange, plus a
 * per-row is_flagged the page uses to render the Flag/Unflag toggle without a follow-up
 * round trip. Backed by rag/messages.py's read_exchanges() annotated with
 * manager_review.is_flagged_map(). */
export interface QuestionFeedItem extends Exchange {
  is_flagged: boolean;
}

export interface QuestionFeedPage {
  total: number;
  exchanges: QuestionFeedItem[];
}

/** On-demand asker reveal for a single Manager Question Feed row. Returned by
 * `/api/admin/questions/{message_id}/asker`, which -- unlike the list endpoint that
 * null-outs asker fields for managers via manager_scope.redact_askers() -- returns the
 * real values one at a time and writes an admin_action_log audit row per non-admin call.
 * `user_email`/`user_oid` are `null` when the underlying message row itself has no caller
 * identity (some early messages were saved without one); `display_name` is `null` only
 * when the email is `null` too, otherwise the manager_scope.resolve_display_name() fallback
 * always produces at least a titleized email-local-part rendering. */
export interface RevealedAsker {
  message_id: string;
  user_email: string | null;
  user_oid: string | null;
  display_name: string | null;
}

export interface McpInfo {
  server_url: string;
  auth_type: string;
  authorize_url: string | null;
  token_url: string | null;
  scopes: string | null;
  resource: string;
  tenant_id: string | null;
}

export interface KnowledgeChunk {
  id: string;
  text: string;
  metadata: Record<string, unknown>;
  distance: number | null;
  /** Derived from the chunk id -- lets the UI offer "flag the whole document" too. */
  doc_id: string;
}

export interface DocumentView {
  doc_id: string;
  metadata: Record<string, unknown>;
  chunks: { id: string; text: string; deleted: boolean }[];
}

export type DeletionScope = "chunk" | "document";
export type DeletionStatus = "pending" | "approved" | "denied" | "undone";

export interface DeletionRequest {
  id: string;
  scope: DeletionScope;
  target_ids: string[];
  preview: Record<string, unknown>;
  reason: string;
  requested_by: string;
  requested_at: string;
  status: DeletionStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
}

export type AdditionStatus = "pending" | "approved" | "denied" | "undone";

export interface AdditionRequest {
  id: string;
  title: string;
  text: string;
  tags: string[];
  filename: string;
  requested_by: string;
  requested_at: string;
  status: AdditionStatus;
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  doc_id: string | null;
  /** The uploader's claimed date for the document itself, "YYYY-MM-DD" -- null for requests
   * submitted before this field existed. */
  document_date: string | null;
  /** Freeform provenance label the uploader typed into the Add-knowledge form ("Brandland",
   * "brand website", "personal knowledge", ...) -- migration 0033, feature request
   * 2026-08-27. Null for requests submitted before the field existed. */
  source_label: string | null;
}

export interface AdditionRequestChunk {
  id: string;
  text: string;
  /** Which section of the source text this chunk carries forward -- e.g. "2026 Holidays -
   * Food & Supply Source" -- null when the chunk doesn't sit under a detected heading. */
  section_heading: string | null;
}

export interface AdditionRequestPreview {
  doc_id: string;
  chunks: AdditionRequestChunk[];
}

// ---------------------------------------------------------------- calls

export const api = {
  me: () => request<Me>("/api/me"),

  ask: (query: string, conversationId?: string | null) =>
    request<Answer>("/api/query", {
      method: "POST",
      body: JSON.stringify({ query, conversation_id: conversationId ?? null }),
    }),

  conversations: (includeArchived = false) =>
    request<ConversationSummary[]>(`/api/conversations?include_archived=${includeArchived}`),

  createConversation: (title?: string) =>
    request<{ id: string }>("/api/conversations", {
      method: "POST",
      body: JSON.stringify({ title: title ?? null }),
    }),

  conversation: (id: string) => request<Conversation>(`/api/conversations/${id}`),

  renameConversation: (id: string, title: string) =>
    request<void>(`/api/conversations/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),

  /** Archives, never deletes -- retention is indefinite (ADR-0009). */
  archiveConversation: (id: string, archived = true) =>
    request<void>(`/api/conversations/${id}/archive`, {
      method: "POST",
      body: JSON.stringify({ archived }),
    }),

  setMessageFeedback: (messageId: string, rating: "up" | "down", comment?: string | null) =>
    request<void>(`/api/messages/${messageId}/feedback`, {
      method: "POST",
      body: JSON.stringify({ rating, comment: comment ?? null }),
    }),

  /** The undo for an accidental thumbs click. */
  clearMessageFeedback: (messageId: string) =>
    request<void>(`/api/messages/${messageId}/feedback`, { method: "DELETE" }),

  adminMessages: (limit = 100, offset = 0, channel?: string, user?: string, tag?: string) =>
    request<ExchangePage>(
      `/api/admin/messages?${new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        ...(channel ? { channel } : {}),
        ...(user ? { user } : {}),
        ...(tag ? { tag } : {}),
      })}`,
    ),

  adminMessageChannelCounts: () =>
    request<ChannelCount[]>("/api/admin/messages/channel-counts"),

  adminMessageUsers: () => request<MessageUser[]>("/api/admin/messages/users"),

  adminQueryTags: () => request<QueryTagDefinition[]>("/api/admin/query-tags"),

  createQueryTag: (slug: string, label: string, description: string) =>
    request<{ ok: boolean; slug: string }>("/api/admin/query-tags", {
      method: "POST",
      body: JSON.stringify({ slug, label, description }),
    }),

  updateQueryTag: (
    slug: string, body: { label?: string; description?: string; active?: boolean },
  ) =>
    request<void>(`/api/admin/query-tags/${encodeURIComponent(slug)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  /** A knowledge manager's manual correction: adds a tag the auto-tagger missed. Rejected past
   * the 3-tag-per-message cap. */
  addMessageTag: (messageId: string, tag: string) =>
    request<void>(`/api/admin/messages/${messageId}/tags`, {
      method: "POST",
      body: JSON.stringify({ tag }),
    }),

  removeMessageTag: (messageId: string, tag: string) =>
    request<void>(`/api/admin/messages/${messageId}/tags/${encodeURIComponent(tag)}`, {
      method: "DELETE",
    }),

  /** Reassign: replaces a question's tags with exactly `tags`, atomically. Prefer this over a
   * batch of addMessageTag/removeMessageTag calls, which can half-apply against the 3-tag cap
   * and leave a question with tags nobody chose -- or with none at all, which hides it from
   * every manager. Adding a tag the caller doesn't own also flags the question into the
   * receiving team's Manager Inbox. Managers may not empty the set; they get a 400. */
  setMessageTags: (messageId: string, tags: string[]) =>
    request<{ ok: boolean; added: string[]; removed: string[] }>(
      `/api/admin/messages/${messageId}/tags`,
      { method: "PUT", body: JSON.stringify({ tags }) },
    ),

  adminDashboardTagStats: (start?: string, end?: string) =>
    request<TagStat[]>(
      `/api/admin/dashboard/tags?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      })}`,
    ),

  /** All-time question counts per tag -- manager-visible, unlike adminDashboardTagStats() above,
   * which is admin-only (it backs the Dashboard). Backs the Query Tags page's usage list. */
  adminQueryTagUsage: () => request<TagStat[]>("/api/admin/query-tags/usage"),

  /** Manager Topic Insights page: one aggregate row per active query-topic tag, all-time.
   * See rag/topic_insights.py for the per-metric privacy rationale; the view splits the
   * response into "Your topics" and "Other topics" cards using assigned_to_viewer. */
  topicInsights: () => request<TopicInsightsResponse>("/api/admin/topic-insights"),

  /** `excludeStatsUsers` drops gaps only excluded testers ever hit. The Gaps page and Manager
   * Inbox leave it off (a hole is a hole, whoever found it); only the Dashboard's top-5 card
   * turns it on, so that list agrees with the tester-excluded count printed beside it. */
  adminGaps: (includeResolved = false, excludeStatsUsers = false) =>
    request<Gap[]>(
      `/api/admin/gaps?include_resolved=${includeResolved}`
      + `&exclude_stats_users=${excludeStatsUsers}`,
    ),

  setGapStatus: (
    gapKey: string, status: "open" | "resolved",
    reason?:
      | "content_added" | "was_out_of_scope" | "duplicate" | "wont_fix" | "code_fix"
      | "answer_sufficient",
    note?: string,
  ) =>
    request<void>(`/api/admin/gaps/${encodeURIComponent(gapKey)}/status`, {
      method: "POST",
      body: JSON.stringify({ status, reason: reason ?? null, note: note ?? null }),
    }),

  /** "The answer was already fine." Resolves the gap AND stops semantically similar questions
   * being flagged in future -- unlike setGapStatus, whose reasons all mean something changed. */
  markGapSufficient: (gapKey: string, note?: string) =>
    request<void>(`/api/admin/gaps/${encodeURIComponent(gapKey)}/sufficient`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  /** Withdraw the verdict above and reopen the gap. Both halves, deliberately: revoking the
   * suppression while leaving the gap closed would hide it and re-flag it at the same time. */
  revokeGapSufficient: (gapKey: string) =>
    request<{ ok: boolean; revoked: boolean }>(
      `/api/admin/gaps/${encodeURIComponent(gapKey)}/sufficient/revoke`, { method: "POST" },
    ),

  /** `excludeStatsUsers` behaves exactly as it does on adminGaps() above. */
  adminOutOfScope: (limit = 200, offset = 0, excludeStatsUsers = false) =>
    request<OutOfScopeCluster[]>(
      `/api/admin/out-of-scope?limit=${limit}&offset=${offset}`
      + `&exclude_stats_users=${excludeStatsUsers}`,
    ),

  /** Hide a cluster that was only out of scope because a scope predicate misfired, now fixed in
   * code. No status/reason argument (unlike setGapStatus) and no unresolve: the cluster comes
   * back by itself if the same question is misclassified again after the fix. */
  resolveOutOfScope: (gapKey: string, note?: string) =>
    request<void>(`/api/admin/out-of-scope/${encodeURIComponent(gapKey)}/resolve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  adminScanRuns: (source?: string, limit = 20) =>
    request<ScanRun[]>(
      `/api/admin/scan-runs?${new URLSearchParams({
        limit: String(limit),
        ...(source ? { source } : {}),
      })}`,
    ),

  /** Shown on the Atlassian Data and Egnyte Data pages; Managers can read it too. */
  adminScanSchedule: () => request<ScanSchedule>("/api/admin/scan-schedule"),

  adminEgnyteRuns: (limit = 20) =>
    request<ScanRun[]>(`/api/admin/egnyte/runs?${new URLSearchParams({ limit: String(limit) })}`),

  adminEgnyteFiles: () => request<EgnyteFile[]>("/api/admin/egnyte/files"),

  /** Starts an Egnyte-only scan in the background; 409 if one is already running. */
  adminEgnyteScan: () => request<{ started: boolean }>("/api/admin/egnyte/scan", { method: "POST" }),

  /** The nightly org chart import from Egnyte's ADPOrgChart folder. */
  adminEgnyteOrgChart: () => request<OrgChartImportStatus>("/api/admin/egnyte/org-chart"),

  /** Confirms the removal of a file that's gone from the Egnyte folder. */
  adminEgnyteRemove: (groupId: string) =>
    request<{ group_id: string; path: string; chunks_removed: number; note: string | null }>(
      `/api/admin/egnyte/files/${encodeURIComponent(groupId)}/remove`,
      { method: "POST" },
    ),

  adminFeedback: (
    rating?: "up" | "down", status?: "waiting" | "addressed" | "ignored", limit = 100, offset = 0,
  ) =>
    request<MessageFeedbackEntry[]>(
      `/api/admin/feedback?limit=${limit}&offset=${offset}${rating ? `&rating=${rating}` : ""}${
        status ? `&status=${status}` : ""
      }`,
    ),

  setFeedbackStatus: (
    messageId: string, status: "waiting" | "addressed" | "ignored", note?: string,
  ) =>
    request<void>(`/api/admin/feedback/${encodeURIComponent(messageId)}/status`, {
      method: "POST",
      body: JSON.stringify({ status, note: note ?? null }),
    }),

  /** Omit both dates for an all-time total (the Feedback page's summary strip); pass a range
   * for the Admin Dashboard's date-scoped Feedback satisfaction tile. */
  /** `excludeStatsUsers` is on for the Dashboard's satisfaction tile (a tester breaking answers
   * on purpose isn't a satisfaction signal) and off for the Feedback page's own strip, which is
   * a worklist and should keep every rating. */
  adminFeedbackCounts: (start?: string, end?: string, excludeStatsUsers = false) =>
    request<FeedbackCount[]>(
      `/api/admin/feedback/counts?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        exclude_stats_users: String(excludeStatsUsers),
      })}`,
    ),

  adminFeedbackStatusCounts: () =>
    request<FeedbackStatusCount[]>("/api/admin/feedback/status-counts"),

  /** `excludeStatsUsers` defaults on, matching the endpoint. RoiEstimateView is the one caller
   * that passes false: an excluded tester is still a person who would have needed a paid seat,
   * so their billable user-months are real savings. Note that llm_cost is never filtered either
   * way -- see dashboard_overview()'s docstring for why the cost figures stay whole. */
  adminDashboardOverview: (start?: string, end?: string, excludeStatsUsers = true) =>
    request<DashboardOverview>(
      `/api/admin/dashboard/overview?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        exclude_stats_users: String(excludeStatsUsers),
      })}`,
    ),

  /** Same row shape as adminMessageUsers(), scoped to a date range and capped to the top N. */
  adminDashboardTopUsers: (start?: string, end?: string, limit = 20) =>
    request<MessageUser[]>(
      `/api/admin/dashboard/top-users?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        limit: String(limit),
      })}`,
    ),

  adminDashboardGapStats: () => request<DashboardGapStats>("/api/admin/dashboard/gaps"),

  adminDashboardKnowledgeGapRate: () =>
    request<DashboardKnowledgeGapRate>("/api/admin/dashboard/knowledge-gap-rate"),

  adminDashboardUiKnowledgeActivity: (start?: string, end?: string) =>
    request<DashboardUiKnowledgeActivity>(
      `/api/admin/dashboard/ui-knowledge-activity?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      })}`,
    ),

  adminDashboardOutOfScopeStats: () =>
    request<DashboardOutOfScopeStats>("/api/admin/dashboard/out-of-scope"),

  adminDashboardKnowledgeStats: () =>
    request<DashboardKnowledgeStats>("/api/admin/dashboard/knowledge"),

  adminDashboardFeedbackByConversation: (limit = 20) =>
    request<ConversationFeedback[]>(`/api/admin/dashboard/feedback-by-conversation?limit=${limit}`),

  adminDashboardTimeseries: (start?: string, end?: string) =>
    request<TimeseriesPoint[]>(
      `/api/admin/dashboard/timeseries?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      })}`,
    ),

  adminDashboardLatency: (start?: string, end?: string) =>
    request<DashboardLatency>(
      `/api/admin/dashboard/latency?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
      })}`,
    ),

  /** The questions in one histogram bucket. `upperMs` omitted means the open-ended top bucket. */
  adminDashboardLatencyQuestions: (
    lowerMs: number, upperMs?: number | null, start?: string, end?: string, limit = 100,
  ) =>
    request<LatencyQuestion[]>(
      `/api/admin/dashboard/latency/questions?${new URLSearchParams({
        lower_ms: String(lowerMs),
        ...(upperMs != null ? { upper_ms: String(upperMs) } : {}),
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        limit: String(limit),
      })}`,
    ),

  adminDashboardAuditLog: (
    start?: string, end?: string, eventType?: string, user?: string, tag?: string,
    limit = 50, offset = 0,
  ) =>
    request<AuditLog>(
      `/api/admin/dashboard/audit-log?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(eventType ? { event_type: eventType } : {}),
        ...(user ? { user } : {}),
        ...(tag ? { tag } : {}),
        limit: String(limit),
        offset: String(offset),
      })}`,
    ),

  /** Every matching event, unpaginated, as a downloadable .xlsx -- backs the Audit Log tab's
   * "Download to Excel" button. Respects whatever activity/user/tag filters are passed in,
   * exactly like adminDashboardAuditLog() above; `start`/`end` are the one dimension callers vary
   * independently of the on-screen filters (omit both for "download all").
   *
   * `includeAskers` fills the User column on question and feedback rows, which are otherwise
   * blank because the asker is redacted by default everywhere in Velma. Passing true is a
   * recorded disclosure: the backend writes an `asker_bulk_revealed` row to the audit trail,
   * the same way the per-row Reveal button writes `asker_revealed`. Default false, so a caller
   * that doesn't think about it gets the anonymised file. */
  adminDashboardAuditLogExport: async (
    start?: string, end?: string, eventType?: string, user?: string, tag?: string,
    includeAskers = false,
  ): Promise<AuditLogExportResult> => {
    const response = await requestBlob(
      `/api/admin/dashboard/audit-log/export?${new URLSearchParams({
        ...(start ? { start } : {}),
        ...(end ? { end } : {}),
        ...(eventType ? { event_type: eventType } : {}),
        ...(user ? { user } : {}),
        ...(tag ? { tag } : {}),
        ...(includeAskers ? { include_askers: "true" } : {}),
      })}`,
    );
    return {
      blob: await response.blob(),
      truncated: response.headers.get("X-Export-Truncated") === "true",
    };
  },

  tags: () => request<string[]>("/api/admin/tags"),

  mcpInfo: () => request<McpInfo>("/api/admin/mcp"),

  admins: () => request<Admin[]>("/api/admin/admins"),

  /** The Users page's merged directory: Admins, Managers and plain callers in one filtered,
   * sorted list. Admin-only, like the rest of that page. */
  adminPeople: (filters: UserDirectoryFilters = {}) =>
    request<UserDirectory>(`/api/admin/people?${userDirectoryParams(filters)}`),

  /** The same list as an .xlsx. Takes the identical filters so the download matches whatever
   * the page is showing; the caller turns the Blob into a save (see AuditLogView's pattern). */
  adminPeopleExport: async (filters: UserDirectoryFilters = {}): Promise<Blob> => {
    const response = await requestBlob(`/api/admin/people/export?${userDirectoryParams(filters)}`);
    return response.blob();
  },

  /** Who the Admin Dashboard leaves out of its statistics. Admin-only in all three verbs. */
  adminStatsExclusions: () => request<StatsExclusion[]>("/api/admin/stats-exclusions"),

  /** Idempotent -- re-adding someone already excluded just re-attributes it, so the picker can
   * send its whole intended set without checking what's already there. */
  adminStatsExclusionAdd: (email: string) =>
    request<StatsExclusion>("/api/admin/stats-exclusions", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  adminStatsExclusionRemove: (email: string) =>
    request<{ email: string; removed: boolean }>(
      `/api/admin/stats-exclusions/${encodeURIComponent(email)}`,
      { method: "DELETE" },
    ),

  actAsTargets: () => request<ActAsTarget[]>("/api/admin/act-as/targets"),

  /** Adds a new Admin/Manager, or -- since the endpoint upserts on email -- changes an
   * existing one's role in place. */
  addAdmin: (email: string, role: Role = "admin") =>
    request<void>("/api/admin/admins", { method: "POST", body: JSON.stringify({ email, role }) }),

  removeAdmin: (email: string) =>
    request<void>(`/api/admin/admins/${encodeURIComponent(email)}`, { method: "DELETE" }),

  setManagerTags: (email: string, tags: string[]) =>
    request<{ ok: boolean; tags: string[] }>(
      `/api/admin/admins/${encodeURIComponent(email)}/tags`,
      { method: "PUT", body: JSON.stringify({ tags }) },
    ),

  /** Paginated fetch of the Manager Inbox list. All params optional:
   * `kind` narrows to a filter tab (all | gaps | thumbs | flagged; matches the
   * backend's KIND_FILTER_* constants in assigned_work.py). `tag` narrows to one
   * query topic within the caller's scope. `limit` + `offset`
   * page through that filter's results -- pass both together and rely on
   * assignedWorkCount({ kind, tag }) for the total (needed for "N of M" display and
   * disabling Next on the last page). Called with no params it returns the
   * full "all" set, preserving the pre-2026-08-30 contract. */
  assignedWork: (params?: {
    kind?: string;
    tag?: string;
    limit?: number;
    offset?: number;
    includeCantFix?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (params?.kind && params.kind !== "all") query.set("kind", params.kind);
    if (params?.tag) query.set("tag", params.tag);
    if (params?.limit != null) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    if (params?.includeCantFix) query.set("include_cant_fix", "true");
    const qs = query.toString();
    return request<AssignedWorkItem[]>(
      `/api/admin/assigned-work${qs ? `?${qs}` : ""}`,
    );
  },

  /** Total count for a manager under the given kind_filter and tag. The nav badge
   * passes neither -- it counts the manager's whole scope, deliberately unaffected by
   * how the page they're looking at happens to be filtered. The pagination indicator
   * passes all three, so its total always describes the rows on screen -- in particular
   * includeCantFix has to match the paired assignedWork() call or the total counts a
   * different set than the list renders. */
  assignedWorkCount: (params?: {
    kind?: string;
    tag?: string;
    includeCantFix?: boolean;
  }) => {
    const query = new URLSearchParams();
    if (params?.kind && params.kind !== "all") query.set("kind", params.kind);
    if (params?.tag) query.set("tag", params.tag);
    if (params?.includeCantFix) query.set("include_cant_fix", "true");
    const qs = query.toString();
    return request<{ count: number }>(
      `/api/admin/assigned-work/count${qs ? `?${qs}` : ""}`,
    );
  },

  /** The full conversation behind one logged exchange, for the "View chat history" dialog on
   * Manager Inbox, Question Feed and the Query log. Pass `messageId` instead of
   * `conversationId` for single-turn surfaces (MCP rows have no conversation); the backend
   * synthesizes a one-exchange thread from the message.
   *
   * The route still says assigned-work because that page shipped it first, but the handler is
   * not inbox-specific -- it scopes to the caller's assigned tags (unrestricted for an admin)
   * and redacts the asker for everyone. Named for what it returns rather than where it lives,
   * so a Query log call site doesn't read like a bug. */
  conversationThread: (conversationId?: string | null, messageId?: string | null) =>
    request<AssignedThread>(
      `/api/admin/assigned-work/thread?${new URLSearchParams({
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(messageId ? { message_id: messageId } : {}),
      })}`,
    ),

  fillAssignedWork: (body: {
    conversation_id?: string | null;
    message_id?: string | null;
    gap_key?: string | null;
    title: string;
    text?: string;
    source?: string;
    knowledge_tags?: string[];
    document_date?: string;
  }) =>
    request<AssignedWorkFillResult>("/api/admin/assigned-work/fill", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  cantFixAssignedWork: (body: {
    gap_key: string;
    conversation_id?: string | null;
    message_id?: string | null;
    note?: string;
  }) =>
    request<AssignedWorkCantFixResult>("/api/admin/assigned-work/cant-fix", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** The "Can't fix" filter tab's data. Lazy-fetched on tab activate (not on page
   * load) because the set can grow unboundedly over time and most manager sessions
   * only ever look at the main inbox. Server caps at 500 regardless of `limit`.
   * `tag` is the inbox's Tag dropdown, passed so the filter keeps applying here.
   * Options object rather than positionals to match assignedWork/assignedWorkCount
   * above, so the three inbox fetches read the same way at their call sites. */
  assignedWorkCantFixList: (params?: { limit?: number; tag?: string }) =>
    request<AssignedWorkCantFixItem[]>(
      `/api/admin/assigned-work/cant-fix?${new URLSearchParams({
        limit: String(params?.limit ?? 100),
        ...(params?.tag ? { tag: params.tag } : {}),
      })}`,
    ),

  assignedWorkFilledList: (params?: { limit?: number; tag?: string }) =>
    request<AssignedWorkFilledItem[]>(
      `/api/admin/assigned-work/filled?${new URLSearchParams({
        limit: String(params?.limit ?? 100),
        ...(params?.tag ? { tag: params.tag } : {}),
      })}`,
    ),

  reopenCantFixAssignedWork: (body: {
    gap_key: string;
    conversation_id?: string | null;
    message_id?: string | null;
  }) =>
    request<AssignedWorkReopenResult>("/api/admin/assigned-work/reopen-cant-fix", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  markReviewedAssignedWork: (messageId: string) =>
    request<{ ok: boolean; message_id: string }>("/api/admin/assigned-work/mark-reviewed", {
      method: "POST",
      body: JSON.stringify({ message_id: messageId }),
    }),

  undoAssignedWork: (body: {
    // 'skip' is intentionally absent -- the "I don't know" button was removed 2026-08-30
    // per user request so items stay visible in the manager inbox instead of being hidden.
    // The backend endpoint + undo('skip') path remain intact for now (see the parallel
    // comment on LastAction in AssignedWorkView.vue).
    action: "fill" | "cant_fix" | "mark_reviewed" | "reopen_cant_fix";
    conversation_id?: string | null;
    message_id?: string | null;
    gap_key?: string | null;
    feedback_message_ids?: string[];
    review_message_ids?: string[];
    fill_request_id?: string | null;
    /** Only used when action='reopen_cant_fix' -- the prior wont_fix note the reopen
     * returned, forwarded back so the re-marked cant-fix restores the same triage
     * rationale rather than dropping it. Ignored for the other actions. */
    note?: string | null;
  }) =>
    request<{ ok: boolean; action: string }>("/api/admin/assigned-work/undo", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  /** The Manager Question Feed page's list -- every tagged assistant message in the caller's
   * scope, newest first, annotated with is_flagged so the row can render the Flag/Unflag
   * toggle without a follow-up round trip. */
  questionFeed: (limit = 50, offset = 0, channel?: string, tag?: string) =>
    request<QuestionFeedPage>(
      `/api/admin/questions?${new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        ...(channel ? { channel } : {}),
        ...(tag ? { tag } : {}),
      })}`,
    ),

  flagQuestion: (messageId: string, note?: string) =>
    request<{ ok: boolean }>(`/api/admin/questions/${encodeURIComponent(messageId)}/flag`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  unflagQuestion: (messageId: string) =>
    request<{ ok: boolean }>(`/api/admin/questions/${encodeURIComponent(messageId)}/flag`, {
      method: "DELETE",
    }),

  revealQuestionAsker: (messageId: string) =>
    request<RevealedAsker>(
      `/api/admin/questions/${encodeURIComponent(messageId)}/asker`,
    ),

  searchChunks: (query: string, k = 25) =>
    request<KnowledgeChunk[]>("/api/admin/chunks/search", {
      method: "POST",
      body: JSON.stringify({ query, k }),
    }),

  documentChunks: (docId: string) =>
    request<DocumentView>(`/api/admin/documents/${encodeURIComponent(docId)}/chunks`),

  createDeletionRequest: (scope: DeletionScope, targetIds: string[], reason: string) =>
    request<DeletionRequest>("/api/admin/deletion-requests", {
      method: "POST",
      body: JSON.stringify({ scope, target_ids: targetIds, reason }),
    }),

  deletionRequests: (status?: DeletionStatus) =>
    request<DeletionRequest[]>(`/api/admin/deletion-requests${status ? `?status=${status}` : ""}`),

  approveDeletionRequest: (id: string, note?: string) =>
    request<DeletionRequest>(`/api/admin/deletion-requests/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  denyDeletionRequest: (id: string, note?: string) =>
    request<DeletionRequest>(`/api/admin/deletion-requests/${id}/deny`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  undoDeletionRequest: (id: string) =>
    request<DeletionRequest>(`/api/admin/deletion-requests/${id}/undo`, { method: "POST" }),

  createAdditionRequest: (body: {
    text: string; title?: string; tags: string[]; filename?: string; document_date?: string;
    /** Freeform provenance label ("Brandland", "brand website", "personal knowledge", ...) --
     * see AdditionRequest.source_label above. */
    source_label?: string;
  }) =>
    request<AdditionRequest>("/api/admin/addition-requests", {
      method: "POST",
      body: JSON.stringify(body),
    }),

  additionRequests: (status?: AdditionStatus) =>
    request<AdditionRequest[]>(`/api/admin/addition-requests${status ? `?status=${status}` : ""}`),

  additionRequestChunks: (id: string) =>
    request<AdditionRequestPreview>(`/api/admin/addition-requests/${id}/chunks`),

  approveAdditionRequest: (id: string, note?: string) =>
    request<AdditionRequest>(`/api/admin/addition-requests/${id}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  denyAdditionRequest: (id: string, note?: string) =>
    request<AdditionRequest>(`/api/admin/addition-requests/${id}/deny`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),

  undoAdditionRequest: (id: string) =>
    request<AdditionRequest>(`/api/admin/addition-requests/${id}/undo`, { method: "POST" }),
};

export interface McpPing {
  ok: boolean;
  ms: number;
  detail: string;
}

/** Pings the MCP adapter's liveness route (ADR-0010). Deliberately outside `request`: /healthz
 *  is unauthenticated -- the same check a kubelet probe makes -- so a failure here means the
 *  endpoint is down, never that a token expired. */
export async function pingMcpHealth(): Promise<McpPing> {
  const started = performance.now();
  try {
    const response = await fetch(`${BASE}/healthz`, { cache: "no-store" });
    const ms = Math.round(performance.now() - started);
    return {
      ok: response.ok,
      ms,
      detail: `HTTP ${response.status}${response.ok ? "" : ` ${response.statusText}`}`,
    };
  } catch (e) {
    return {
      ok: false,
      ms: Math.round(performance.now() - started),
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}
